import CssBaseline from '@mui/material/CssBaseline';
import React from 'react';
import { SvgIconCacheLibrary } from '@cp/ds/src/components/svgIcon/components/cache/library';
import { BreadcrumbsContext } from '@cp/ds/src/components/breadcrumbs';
import { Helmet } from 'react-helmet';
import { useInjection } from '@cp/shared/di';
import { HelmetContextProvider } from 'src/shared/helmet';
import { TrackingProvider } from 'src/app/providers/tracking/trackingProvider';
import { CONFIG_MANAGER_TOKEN, ConfigContextProvider } from './config';
import { AppSuggestionsProvider } from './suggestions';
import { CatalogItemViewsProvider } from './catalogItemViews';
import { AppDeclinationProvider } from './declination';
import { RouterProvider } from './router';
import { ClientWebRouteResolver } from './routeResolver';
import { AppModalsResolver } from './modal';
import { TrademarkProvider } from './trademark';
import { EventBusProvider } from './eventBus';
import { PushNotificationsProvider } from './pushNotifications';
import { ActionRunner } from './actionRunner';
import { EmbeddingIntegrationProvider } from './embedding';
import { VarioqubProvider } from './varioqub';
import { ThemeProvider } from './theme';

export interface ProvidersProps {
  ssrMatchMedia?: (query: string) => {
    matches: boolean;
  };
}

export const Providers: React.FC<React.PropsWithChildren<ProvidersProps>> = ({ children, ssrMatchMedia }) => {
  const config = useInjection(CONFIG_MANAGER_TOKEN);
  return (
    <ThemeProvider ssrMatchMedia={ssrMatchMedia}>
      {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
      <CssBaseline />
      <SvgIconCacheLibrary />
      <ConfigContextProvider>
        <HelmetContextProvider value={Helmet}>
          <ClientWebRouteResolver>
            <EventBusProvider>
              <RouterProvider>
                <VarioqubProvider>
                  <TrademarkProvider>
                    <AppModalsResolver>
                      <AppDeclinationProvider>
                        <AppSuggestionsProvider>
                          <CatalogItemViewsProvider>
                            <BreadcrumbsContext.Provider value={{ hostname: config.getOrThrow('CLIENT_WEB_ORIGIN') }}>
                              <PushNotificationsProvider>
                                <TrackingProvider>
                                  <EmbeddingIntegrationProvider>
                                    {children}
                                    <ActionRunner />
                                  </EmbeddingIntegrationProvider>
                                </TrackingProvider>
                              </PushNotificationsProvider>
                            </BreadcrumbsContext.Provider>
                          </CatalogItemViewsProvider>
                        </AppSuggestionsProvider>
                      </AppDeclinationProvider>
                    </AppModalsResolver>
                  </TrademarkProvider>
                </VarioqubProvider>
              </RouterProvider>
            </EventBusProvider>
          </ClientWebRouteResolver>
        </HelmetContextProvider>
      </ConfigContextProvider>
    </ThemeProvider>
  );
};
